import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Shield, Activity, HeartHandshake } from "lucide-react";
import Seo from "../lib/seo";
import { PETITION_URL } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import PetitionCTA from "../components/PetitionCTA";
import SocialShare from "../components/SocialShare";
import ShareStorySection from "../components/ShareStorySection";
import ActionBar from "../components/ActionBar";

// Animated red heartbeat divider
const RedHeartbeat = ({ className = "" }) => (
    <div className={`flex items-center gap-3 my-6 ${className}`}>
        <div className="flex-1 h-px bg-coral/40" />
        <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, type: "spring" }}
            className="w-10 h-10 rounded-full border-2 border-coral flex items-center justify-center bg-white"
        >
            <Heart className="w-4 h-4 text-coral animate-heartbeat" fill="currentColor" />
        </motion.div>
        <div className="flex-1 h-px bg-coral/40" />
    </div>
);

// Red-iconed bio item — magazine pull-out card
const RedItem = ({ icon: Icon, title, children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
        className="grid grid-cols-[auto_1fr] gap-5 md:gap-7 items-start py-7 border-b border-coral/15 last:border-b-0"
    >
        <motion.div
            whileHover={{ scale: 1.1, rotate: -4 }}
            transition={{ type: "spring", stiffness: 300, damping: 16 }}
            className="w-14 h-14 md:w-16 md:h-16 border-2 border-coral rounded-full flex items-center justify-center bg-white shadow-sm flex-shrink-0"
        >
            <Icon className="w-6 h-6 md:w-7 md:h-7 text-coral" strokeWidth={1.6} />
        </motion.div>
        <div>
            <h3 className="font-sans font-bold uppercase tracking-[0.18em] text-base md:text-lg text-coral mb-2">
                {title}
            </h3>
            <p className="text-base md:text-lg text-navy-800/85 leading-relaxed">
                {children}
            </p>
        </div>
    </motion.div>
);

export default function JCStoryPage() {
    return (
        <>
            <Seo
                title="Our Story — JC's Legacy"
                description="The story of Joshua 'JC' — a father, partner, and protector. JC Law was created to turn tragedy into prevention and help save lives when every second matters."
                path="/our-story"
            />

            <PageHeader
                overline="Our Story"
                title="This is why we fight. This is why we advocate."
                subtitle="In loving memory of Joshua 'JC' — a father, partner, and protector whose legacy lives on through this mission."
                image="/images/jc-real.png"
            />

            {/* Editorial Magazine Section */}
            <section className="bg-editorial py-24 md:py-32 relative overflow-hidden" data-testid="story-magazine">
                <div className="absolute inset-0 bg-noise opacity-20 pointer-events-none" />
                <div className="max-w-[900px] mx-auto px-6 lg:px-10 relative">
                    {/* Magazine-style title */}
                    <Reveal>
                        <h2 className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[1] text-center uppercase">
                            <span className="text-navy-900">Our </span>
                            <span className="text-coral italic">Story</span>
                        </h2>
                        <RedHeartbeat />
                        <p className="text-center font-sans uppercase tracking-[0.22em] text-sm text-navy-800 mb-10">
                            This is why we fight. This is why we advocate.
                        </p>
                    </Reveal>

                    {/* Opening narrative */}
                    <div className="space-y-6">
                        <Reveal delay={0.1}>
                            <p className="text-xl md:text-2xl text-navy-900 leading-relaxed font-serif italic">
                                <motion.span
                                    initial={{ color: "#0A1128" }}
                                    whileInView={{ color: "#E63946" }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: 0.3 }}
                                    className="font-semibold not-italic font-sans uppercase tracking-wider text-base"
                                >
                                    April 20, 2026 —
                                </motion.span>{" "}
                                I lost my partner unexpectedly due to cardiac arrest while staying in a hotel.
                            </p>
                        </Reveal>

                        <Reveal delay={0.15}>
                            <p className="text-lg text-navy-800/85 leading-relaxed">
                                Joshua "JC" was a father of six who had nothing but love and compassion to give. He dedicated his life to protecting others and making his community a safer place for everyone.
                            </p>
                        </Reveal>

                        <Reveal delay={0.2}>
                            <p className="text-lg text-navy-800/85 leading-relaxed">
                                JC was the kind of person who showed up for people without hesitation. His strength, courage, and heart left a lasting impact on everyone who knew him.
                            </p>
                        </Reveal>

                        {/* Personal photos of JC — magazine pull-out */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1, ease: [0.21, 0.47, 0.32, 0.98] }}
                            className="my-12"
                        >
                            <div className="relative group overflow-hidden">
                                <div className="absolute -inset-3 border border-coral/40 pointer-events-none" />
                                <img
                                    src="/images/jc-personal.png"
                                    alt="Joshua 'JC' — personal photos: a winter portrait with his beard and beanie, and a moment of peace at the beach with his Braves cap"
                                    className="w-full h-auto block transition-transform duration-[2000ms] group-hover:scale-[1.02]"
                                    loading="lazy"
                                />
                            </div>
                            <p className="mt-5 font-serif italic text-navy-800/70 text-center text-sm md:text-base">
                                Moments that meant everything — JC, the man, the father, the friend. <span className="text-coral">♡</span>
                            </p>
                        </motion.div>

                        <Reveal delay={0.25}>
                            <p className="text-lg text-navy-800/85 leading-relaxed">
                                In the middle of unimaginable loss, I was faced with the reality of how critical emergency preparedness truly is. No family should ever have to experience the feeling of helplessness during a cardiac emergency without access to trained staff or life-saving equipment.
                            </p>
                        </Reveal>

                        <Reveal delay={0.3}>
                            <p className="text-lg text-navy-800/85 leading-relaxed">
                                His courage, compassion, and commitment to justice continue to inspire this mission every single day. Though he is no longer physically with us, his name and legacy will live on through this movement.
                            </p>
                        </Reveal>
                    </div>

                    {/* Red themed icon list */}
                    <div className="mt-16 pt-8 border-t border-coral/30">
                        <RedItem icon={Heart} title="A Heart of Service" delay={0.05}>
                            JC was the kind of person who showed up for people without hesitation. His strength, courage, and heart left a lasting impact on everyone who knew him.
                        </RedItem>
                        <RedItem icon={Shield} title="A Life Dedicated to Others" delay={0.1}>
                            He dedicated his life to protecting others and making his community a safer place for everyone. His courage, compassion, and commitment to justice continue to inspire us every day.
                        </RedItem>
                        <RedItem icon={Activity} title="Turning Tragedy Into Prevention" delay={0.15}>
                            In the middle of unimaginable loss, I was faced with the reality of how critical emergency preparedness truly is. No family should ever have to experience the feeling of helplessness during a cardiac emergency without access to trained staff or life-saving equipment.
                        </RedItem>
                        <RedItem icon={HeartHandshake} title="A Legacy That Lives On" delay={0.2}>
                            Though he is no longer physically with us, his name and legacy will live on through this movement. JC's life, his values, and his mission continue to drive everything we do.
                        </RedItem>
                    </div>

                    {/* "Every second matters" with red script & lines */}
                    <Reveal delay={0.4}>
                        <div className="mt-16 pt-8 border-t border-coral/30 text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1, ease: "easeOut" }}
                                className="flex items-center justify-center gap-4"
                            >
                                <p
                                    className="font-serif italic text-4xl md:text-5xl lg:text-6xl text-coral"
                                    style={{ fontWeight: 400 }}
                                >
                                    Every second matters.
                                </p>
                                <motion.span
                                    animate={{ scale: [1, 1.15, 1] }}
                                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <Heart className="w-7 h-7 md:w-9 md:h-9 text-coral" fill="currentColor" />
                                </motion.span>
                            </motion.div>
                            <div className="mt-6 h-px bg-coral/40 max-w-2xl mx-auto" />
                            <p className="mt-8 font-serif text-3xl md:text-4xl text-coral font-semibold tracking-tight">JC LAW</p>
                            <p className="mt-2 text-navy-800/75 max-w-md mx-auto">
                                was created to turn tragedy into prevention — and to help save lives when every second matters.
                            </p>
                            <a
                                href={PETITION_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-10 inline-flex items-center justify-center gap-2 px-12 py-4 bg-navy-900 hover:bg-navy-800 text-editorial rounded-full font-sans uppercase tracking-[0.2em] text-xs transition-colors duration-300"
                            >
                                Sign The Petition
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.5}>
                        <div className="mt-16 pt-8 border-t border-editorial-border">
                            <p className="overline text-coral mb-4">Share JC's Story</p>
                            <p className="text-navy-800/80 mb-5">Help spread the mission. Every share extends JC's legacy.</p>
                            <SocialShare testidPrefix="story-share" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Share Your Story */}
            <ShareStorySection />

            {/* Action Bar */}
            <ActionBar />

            <PetitionCTA />
        </>
    );
}
